import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ClipboardList, LogOut, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import api, { errorMessage } from "@/lib/api";

const EMPTY = { client: "", reference: "", due_date: "" };

export default function OrderNew() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSaving(true);
      try {
        const res = await api.post("/orders", form);
        toast.success("Ordem de corte criada", { description: "Adiciona agora os tecidos a cortar." });
        navigate(`/ordens/${res.data.id}`);
      } catch (err) {
        toast.error("Erro ao criar ordem", { description: errorMessage(err) });
      } finally {
        setSaving(false);
      }
    },
    [form, navigate]
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="h-14 shrink-0 border-b border-border flex items-center justify-between px-4 bg-card">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/ordens")}
            className="w-9 h-9 border border-border flex items-center justify-center hover:bg-accent transition-colors"
            title="Voltar às ordens"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="w-8 h-8 bg-foreground flex items-center justify-center">
            <ClipboardList className="w-4 h-4 text-background" />
          </div>
          <div className="leading-none">
            <h1 className="font-head text-sm font-bold tracking-tight">NOVA ORDEM DE CORTE</h1>
            <p className="text-[10px] text-muted-foreground mono mt-0.5">{user?.company_name}</p>
          </div>
        </div>
        <button
          onClick={logout}
          title="Terminar sessão"
          className="w-9 h-9 border border-border flex items-center justify-center hover:bg-accent transition-colors"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </header>

      <main className="p-6 max-w-md mx-auto">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Cliente</Label>
            <Input
              required
              value={form.client}
              onChange={(e) => setForm({ ...form, client: e.target.value })}
              data-testid="order-client-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Referência</Label>
            <Input
              required
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              data-testid="order-reference-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Prazo de entrega</Label>
            <Input
              required
              type="date"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              data-testid="order-duedate-input"
            />
          </div>
          <p className="text-[11px] text-muted-foreground">
            Depois de criares a ordem, adicionas os tecidos/matrizes a cortar (cada um com o seu ficheiro, matriz e nº de camadas).
          </p>
          <Button type="submit" disabled={saving} className="rounded-[4px]" data-testid="order-create-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Criar ordem
          </Button>
        </form>
      </main>
    </div>
  );
}
